import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { Office365CopySource } from 'app/model/pipeline/copysource/office365.copysource';
import { ValueAdapter } from '../../value.adapter';
import { office365UserScopes } from 'app/registries/consts/metadata-consts';
import { NamedArray } from 'app/model/base/namedarray';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { Column } from 'app/model/dataset/column';
import { Owned } from 'app/model/base/named';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Office365CopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        userScopeFilterUri: '*',
        allowedGroups: '*',
        dateFilterColumn: '*',
        startTime: '*',
        endTime: '*',
        outputColumns: '*'
    }
})
export class Office365CopySourceAdapter extends CopySourceAdapter<Office365CopySource, Model.Office365Source> {
    protected override async _toResource(copySource: Office365CopySource): Promise<Model.Office365Source> {
        const result = this.getCommonCopySourceProperties(copySource, Metamodel.modelFor(Office365CopySource).name);
        result.dateFilterColumn = ValueAdapter.toResource(copySource, Office365CopySource.DATEFILTERCOLUMN);
        result.startTime = ValueAdapter.toResource(copySource, Office365CopySource.STARTTIME);
        result.endTime = ValueAdapter.toResource(copySource, Office365CopySource.ENDTIME);
        result.allowedGroups =
            copySource.userScope === office365UserScopes.group
                ? ValueAdapter.toResource(copySource, Office365CopySource.ALLOWEDGROUPS)
                : undefined;
        result.userScopeFilterUri =
            copySource.userScope === office365UserScopes.all
                ? ValueAdapter.toResource(copySource, Office365CopySource.USERSCOPEFILTERURI)
                : undefined;
        const columnsValue = ValueAdapter.toResource(copySource, Office365CopySource.OUTPUT_COLUMNS);

        result.outputColumns =
            columnsValue != null && columnsValue.type !== 'Expression' && Array.isArray(columnsValue)
                ? columnsValue.map((c) => ({ name: c.name }))
                : columnsValue;

        return result;
    }

    protected override async _toModel(
        resource: Model.Office365Source,
        copyActivity: CopyActivity
    ): Promise<Office365CopySource> {
        const result = this.createCopySourceWithCommonProperties(
            Office365CopySource.MODEL,
            resource,
            copyActivity
        ) as Office365CopySource;
        ValueAdapter.toModel(result, Office365CopySource.DATEFILTERCOLUMN, resource.dateFilterColumn);
        ValueAdapter.toModel(result, Office365CopySource.STARTTIME, resource.startTime);
        ValueAdapter.toModel(result, Office365CopySource.ENDTIME, resource.endTime);
        ValueAdapter.toModel(result, Office365CopySource.ALLOWEDGROUPS, resource.allowedGroups);
        ValueAdapter.toModel(result, Office365CopySource.USERSCOPEFILTERURI, resource.userScopeFilterUri);
        result.userScope = resource.allowedGroups ? office365UserScopes.group : office365UserScopes.all;

        const columnValue: NamedArray<Column> = new NamedArray<Column>(result);
        const columns = resource.outputColumns;
        if (columns != null) {
            if (
                (typeof columns === 'string' && isLogicAppExpression(columns)) ||
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (columns as any).type === 'Expression'
            ) {
                ValueAdapter.toModel(result, Office365CopySource.OUTPUT_COLUMNS, columns);
            } else if (Array.isArray(columns)) {
                columns.forEach((column) => {
                    const newColumn: Column = Owned.create<Column>(Metamodel.modelFor(Column), result);
                    newColumn.name = column.name;
                    columnValue.push(newColumn);
                });
            }
            result.outputColumns = columnValue;
        }
        return result;
    }
}
